import {
  ccGetPosition,
  ccGetCards,
  ccGetActions,
  ccConfirmOccurrencePayment,
  ccDismissOccurrenceRecommendation,
} from '../api-client';
import { navigate } from '../router';
import { openBalanceSheet } from './balance-sheet';
import { openCommitmentSheet } from './commitment-sheet';
import { openDrill } from './cc-drill-sheet';
import type { CcPositionResponse, CcWarning, CcDueSoonItem, CcCardItem, DoThisNowAction } from '../api-client';

const HORIZONS = [7, 30, 60, 90] as const;
type Horizon = typeof HORIZONS[number];

function formatAed(n: number): string {
  return `AED ${Math.round(n).toLocaleString('en-US')}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return iso;
  return `${day}.${month}.${year}`;
}

function formatDueDate(iso: string): string {
  return `Due ${formatIsoDate(iso)}`;
}

function warningHtml(w: CcWarning): string {
  return `<div class="cc-warn cc-warn-${escapeHtml(w.kind)}">${escapeHtml(w.message)}</div>`;
}

function dueSoonRowHtml(d: CcDueSoonItem): string {
  const amt = d.currency === 'AED'
    ? formatAed(d.amountNative)
    : `${escapeHtml(d.currency)} ${Math.round(d.amountNative).toLocaleString('en-US')}`;
  return `<div class="cc-due-row">
    <span class="cc-due-name">${escapeHtml(d.name)}</span>
    <span class="cc-due-amt">${amt}</span>
    <span class="cc-due-date">${escapeHtml(formatDueDate(d.dueDate))}</span>
  </div>`;
}

function cardStateBadge(state: CcCardItem['state']): string {
  if (state === 'overdue') return '<span class="cc-card-badge cc-card-badge-overdue">Overdue</span>';
  if (state === 'due_soon') return '<span class="cc-card-badge cc-card-badge-soon">Due soon</span>';
  return '<span class="cc-card-badge cc-card-badge-upcoming">Upcoming</span>';
}

function cardRowHtml(c: CcCardItem): string {
  const amt = c.currency === 'AED'
    ? formatAed(c.amountNative)
    : `${escapeHtml(c.currency)} ${Math.round(c.amountNative).toLocaleString('en-US')}`;
  const min = c.minPayment == null ? 'min —' : `min ${formatAed(c.minPayment)}`;
  return `<div class="cc-card-row cc-card-${escapeHtml(c.state)}">
    <div class="cc-card-main">
      <span class="cc-card-name">${escapeHtml(c.name)}</span>
      <span class="cc-card-amt">${amt}</span>
    </div>
    <div class="cc-card-meta">
      ${cardStateBadge(c.state)}
      <span class="cc-card-due">${escapeHtml(formatDueDate(c.dueDate))}</span>
      <span class="cc-card-min">${min}</span>
    </div>
  </div>`;
}

function cardsSectionHtml(cards: CcCardItem[]): string {
  const rows = cards.length
    ? cards.map(cardRowHtml).join('')
    : '<div class="cc-empty">Nothing hidden</div>';
  return `<div class="cc-cards">
    <div class="cc-section-label">Hidden card actions</div>
    ${rows}
  </div>`;
}

function actionAmountHtml(action: DoThisNowAction): string {
  if (action.amountNative == null) return '';
  const amount = action.currency === 'AED'
    ? formatAed(action.amountNative)
    : `${escapeHtml(action.currency)} ${Math.round(action.amountNative).toLocaleString('en-US')}`;
  return `<span class="cc-now-amount">${amount}</span>`;
}

function actionRowHtml(action: DoThisNowAction): string {
  const amountInput = action.occurrenceId && action.kind === 'pay_occurrence' && action.amountNative != null
    ? `<label class="cc-now-input-label">
        <span>Paid</span>
        <input class="cc-now-amount-input" data-action-amount-input="${escapeHtml(action.occurrenceId)}" type="number" inputmode="decimal" min="0" step="0.01" value="${escapeHtml(String(action.amountNative))}" />
      </label>`
    : '';
  const confirmButton = action.occurrenceId && action.kind === 'pay_occurrence' && action.amountNative != null
    ? `<button class="cc-now-primary" data-action-confirm-occ="${escapeHtml(action.occurrenceId)}" type="button">${escapeHtml(action.confirmationLabel)}</button>`
    : '';
  const refreshButton = action.kind === 'refresh_source'
    ? `<button class="cc-now-primary" data-action-refresh-balance="${escapeHtml(action.sourceAccountId ?? '')}" type="button">${escapeHtml(action.confirmationLabel)}</button>`
    : '';
  const dismissButton = action.occurrenceId
    ? `<button class="cc-now-secondary" data-action-dismiss-occ="${escapeHtml(action.occurrenceId)}" type="button">Hide</button>`
    : '';
  return `<div class="cc-now-row cc-now-${escapeHtml(action.urgency)}">
    <div class="cc-now-main">
      <span class="cc-now-title">${escapeHtml(action.title)}</span>
      ${actionAmountHtml(action)}
    </div>
    <div class="cc-now-subtitle">${escapeHtml(action.subtitle)}</div>
    <div class="cc-now-meta">
      <span>${escapeHtml(formatDueDate(action.dueDate))}</span>
      ${action.sourceName ? `<span>${escapeHtml(action.sourceName)}</span>` : ''}
    </div>
    <div class="cc-now-buttons">${amountInput}${confirmButton}${refreshButton}${dismissButton}</div>
  </div>`;
}

function doThisNowHtml(actions: DoThisNowAction[]): string {
  const rows = actions.length
    ? actions.map(actionRowHtml).join('')
    : '<div class="cc-empty">Nothing needs action right now</div>';
  return `<div class="cc-actions-now">
    <div class="cc-section-label">Do This Now</div>
    ${rows}
  </div>`;
}

function positionHtml(
  p: CcPositionResponse,
  horizon: Horizon,
  cards: CcCardItem[],
  actions: DoThisNowAction[],
): string {
  const h = p.headline;
  const net = h.netAed;
  const tone = net >= 0 ? 'sage' : 'terracotta';
  const mood = net >= 0 ? 'comfortable' : 'short';

  const warnings = p.warnings.length
    ? `<div class="cc-warnstack">${p.warnings.map(warningHtml).join('')}</div>`
    : '';
  const toggles = HORIZONS.map((d) =>
    `<button class="cc-toggle-btn${d === horizon ? ' on' : ''}" data-h="${d}">${d}d</button>`,
  ).join('');
  const dueSoonRows = p.dueSoon.length
    ? p.dueSoon.map(dueSoonRowHtml).join('')
    : '<div class="cc-empty">Nothing due in this window</div>';

  return `
    ${doThisNowHtml(actions)}
    ${warnings}
    <div class="cc-headline">
      <div class="cc-eyebrow">Net after commitments</div>
      <div class="cc-net money-hero ${tone}">${formatAed(net)}</div>
      <div class="cc-mood">${mood} · ${horizon} days</div>
      <div class="cc-toggle">${toggles}</div>
    </div>
    <div class="cc-breakout">
      <button class="cc-line" data-drill="cash">
        <span class="cc-line-label">Cash available</span>
        <span class="cc-line-amt">${formatAed(h.cashAvailableAed)}</span>
      </button>
      <button class="cc-line" data-drill="commitments">
        <span class="cc-line-label">Commitments due</span>
        <span class="cc-line-amt cc-line-out">− ${formatAed(h.commitmentsDueAed)}</span>
      </button>
      <button class="cc-line" data-drill="income">
        <span class="cc-line-label">Confirmed income</span>
        <span class="cc-line-amt cc-line-in">+ ${formatAed(h.confirmedIncomeAed)}</span>
      </button>
      <div class="cc-line cc-line-muted">
        <span class="cc-line-label">Unconfirmed (excl.)</span>
        <span class="cc-line-amt">${formatAed(h.unconfirmedIncomeAed)}</span>
      </div>
    </div>
    ${cardsSectionHtml(cards)}
    <div class="cc-duesoon">
      <div class="cc-section-label">Due soon</div>
      ${dueSoonRows}
    </div>
    <div class="cc-actions">
      <button class="cc-btn" data-act="balances">Update balances</button>
      <button class="cc-btn cc-btn-secondary" data-act="commitment">+ Commitment</button>
    </div>
  `;
}

export function renderControlCentre(root: HTMLElement): void {
  let horizon: Horizon = 30;

  root.innerHTML = `
    <div class="home">
      <header class="home-header">
        <span class="home-hello">Control Centre</span>
        <div class="home-header-actions">
          <a href="/log" class="nav-ico" id="log-link" aria-label="Log">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="10" cy="10" r="7.5"></circle>
              <line x1="10" y1="6.5" x2="10" y2="10.5"></line>
              <line x1="10" y1="10.5" x2="13" y2="10.5"></line>
            </svg>
          </a>
          <a href="/dashboard" class="nav-ico" id="dash-link" aria-label="Dashboard">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2.5" y="5" width="15" height="11" rx="2.5"></rect>
              <path d="M2.5 8.5 h15"></path>
              <circle cx="14" cy="12" r="1"></circle>
            </svg>
          </a>
          <a href="/review" class="nav-ico" id="reports-link" aria-label="Family review">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="4" y1="17" x2="4" y2="9"></line>
              <line x1="10" y1="17" x2="10" y2="3"></line>
              <line x1="16" y1="17" x2="16" y2="12"></line>
            </svg>
          </a>
          <a href="/sms-inbox" class="nav-ico" id="sms-link" aria-label="SMS Inbox">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2.5" y="4" width="15" height="10" rx="2"></rect>
              <path d="M5.5 8h9M5.5 11h5"></path>
            </svg>
          </a>
          <a href="/settings" class="nav-ico" id="gear-link" aria-label="Settings">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="3" y1="7" x2="17" y2="7"></line>
              <circle cx="8" cy="7" r="2.1" fill="var(--bg)"></circle>
              <line x1="3" y1="13" x2="17" y2="13"></line>
              <circle cx="13" cy="13" r="2.1" fill="var(--bg)"></circle>
            </svg>
          </a>
        </div>
      </header>
      <section class="cc"><div class="cc-body sk sk-block">Loading…</div></section>
    </div>
  `;

  // Wire nav links for client-side routing (no full-page reloads)
  const logLink = root.querySelector<HTMLAnchorElement>('#log-link');
  if (logLink) {
    logLink.onclick = (e) => { e.preventDefault(); void navigate('/log'); };
  }
  const dashLink = root.querySelector<HTMLAnchorElement>('#dash-link');
  if (dashLink) {
    dashLink.onclick = (e) => { e.preventDefault(); void navigate('/dashboard'); };
  }
  const reportsLink = root.querySelector<HTMLAnchorElement>('#reports-link');
  if (reportsLink) {
    reportsLink.onclick = (e) => { e.preventDefault(); void navigate('/review'); };
  }
  const smsLink = root.querySelector<HTMLAnchorElement>('#sms-link');
  if (smsLink) {
    smsLink.onclick = (e) => { e.preventDefault(); void navigate('/sms-inbox'); };
  }
  const gearLink = root.querySelector<HTMLAnchorElement>('#gear-link');
  if (gearLink) {
    gearLink.onclick = (e) => { e.preventDefault(); void navigate('/settings'); };
  }

  const body = root.querySelector('.cc-body') as HTMLElement;

  async function load(): Promise<void> {
    body.classList.add('sk', 'sk-block');
    body.textContent = '';
    try {
      const [p, cardsRes, actionsRes] = await Promise.all([
        ccGetPosition(horizon),
        ccGetCards(),
        ccGetActions(),
      ]);
      body.classList.remove('sk', 'sk-block');
      body.innerHTML = positionHtml(p, horizon, cardsRes.cards, actionsRes.actions);

      body.querySelectorAll<HTMLElement>('[data-h]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const h = Number(btn.dataset.h) as Horizon;
          if (HORIZONS.includes(h)) {
            horizon = h;
            void load();
          }
        });
      });

      body.querySelectorAll<HTMLButtonElement>('[data-drill]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const kind = btn.getAttribute('data-drill') as 'cash' | 'commitments' | 'income';
          if (kind === 'cash' || kind === 'commitments' || kind === 'income') {
            openDrill(kind, horizon, () => { void load(); });
          }
        });
      });

      body.querySelectorAll<HTMLButtonElement>('[data-action-confirm-occ]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const occId = btn.getAttribute('data-action-confirm-occ');
          const input = btn.closest<HTMLElement>('.cc-now-row')
            ?.querySelector<HTMLInputElement>('[data-action-amount-input]');
          const amount = Number(input?.value);
          if (!occId || !Number.isFinite(amount) || amount <= 0) return;
          btn.disabled = true;
          try {
            await ccConfirmOccurrencePayment(occId, amount);
            await load();
          } catch {
            btn.disabled = false;
          }
        });
      });

      body.querySelectorAll<HTMLButtonElement>('[data-action-refresh-balance]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const accountId = btn.getAttribute('data-action-refresh-balance') || null;
          openBalanceSheet(load, accountId);
        });
      });

      body.querySelectorAll<HTMLButtonElement>('[data-action-dismiss-occ]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const occId = btn.getAttribute('data-action-dismiss-occ');
          if (!occId) return;
          btn.disabled = true;
          try {
            await ccDismissOccurrenceRecommendation(occId);
            await load();
          } catch {
            btn.disabled = false;
          }
        });
      });

      body.querySelector<HTMLButtonElement>('[data-act="balances"]')
        ?.addEventListener('click', () => openBalanceSheet(load));
      body.querySelector<HTMLButtonElement>('[data-act="commitment"]')
        ?.addEventListener('click', () => openCommitmentSheet(load));
    } catch {
      body.classList.remove('sk', 'sk-block');
      body.innerHTML = `<div class="cc-error">
        Couldn't load your position.
        <button class="ghost" id="cc-retry" type="button">Retry</button>
      </div>`;
      body.querySelector<HTMLButtonElement>('#cc-retry')
        ?.addEventListener('click', () => { void load(); });
    }
  }

  void load();
}
